import { InfoCircleOutlined } from '@ant-design/icons';
import { Chart, type ChartData, type ChartOptions, registerables } from 'chart.js';
import commaNumber from 'comma-number';
import { useMemo } from 'react';
import { Pie } from 'react-chartjs-2';

import { Flex, Space } from '@/components/base';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Heading from '@/components/Typography/Heading';
import { MainText } from '@/components/Typography/NamedTypo';
import useColor from '@/hooks/domain/useColor';

Chart.register(...registerables);

type Props = {
  filteredTotal: number;
  total: number;
};

const FilteredInfoArea = (props: Props) => {
  const color = useColor();

  const pieChartOptions: ChartOptions<'pie'> = useMemo(() => {
    return {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        datalabels: {
          display: false,
        },
      },
    };
  }, []);

  const pieChartData: ChartData<'pie'> = useMemo(() => {
    return {
      labels: ['Filtered', 'Total'],
      datasets: [
        {
          data: [props.filteredTotal, (props.total || 0) - props.filteredTotal],
          backgroundColor: [color.green500, color.green(0.1)],
          borderColor: 'transparent',
        },
      ],
    };
  }, [color, props.filteredTotal, props.total]);

  return (
    <>
      <Flex style={{ height: 80 }}>
        <Pie
          data={pieChartData}
          options={pieChartOptions}
          style={{ height: '100%' }}
        />
      </Flex>
      <Space h="12px" />
      <Flex g="4px" a="flex-end">
        <Heading>
          {commaNumber(
            ((props.filteredTotal / (props.total || 0)) * 100).toFixed(1),
          )}
          %
        </Heading>

        <Tooltip tooltipNode="Ratio based on all feedback during a given time frame.">
          <InfoCircleOutlined
            style={{ color: color.gray600, marginBottom: 2 }}
          />
        </Tooltip>
      </Flex>
      <Space h="4px" />
      <MainText dim>
        <MainText weight="sb">{commaNumber(props.filteredTotal)}</MainText> of{' '}
        {commaNumber(props.total || 0)} feedback
      </MainText>
    </>
  );
};

export default FilteredInfoArea;
